/**
 * useScrollProgress — Continuous scroll-linked transforms for Cogo nodes.
 *
 * Uses Framer Motion's useScroll + useTransform to map scroll position
 * to visual properties (opacity, translateY, scale, blur, etc.).
 *
 * Use cases: parallax, navbar glass effects, progress-based reveals.
 */
import { useMemo, useRef } from 'react'
import { useScroll, useTransform, type MotionValue } from 'motion/react'
import type { AnimationConfig } from '../core/types'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface UseScrollProgressResult {
  ref: React.RefObject<HTMLDivElement | null>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style: Record<string, MotionValue<any>> | null
  isScrollProgress: boolean
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Scroll-progress hook for Cogo nodes.
 *
 * When animation.scroll.trigger === "progress" AND animation.scrollProgress
 * is defined, this hook maps scroll position → visual properties.
 *
 * Returns motion style values that can be spread onto a motion.div.
 */
export function useScrollProgress(
  animation: AnimationConfig | undefined,
): UseScrollProgressResult {
  const ref = useRef<HTMLDivElement | null>(null)

  const isScrollProgress =
    animation?.scroll?.trigger === 'progress' &&
    animation?.scrollProgress != null

  const offset = animation?.scroll?.offset ?? ['start end', 'end start']
  const props = animation?.scrollProgress

  const { scrollYProgress } = useScroll({
    target: isScrollProgress ? ref : undefined,
    // Framer Motion expects specific string literal unions; our config uses plain strings
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    offset: offset as any,
  })

  // Call all useTransform hooks unconditionally with safe defaults to satisfy rules-of-hooks.
  // Final style record is assembled via useMemo, picking only the entries whose props are present.
  const opacityMV = useTransform(scrollYProgress, [0, 1], props?.opacity ?? [0, 0])
  const translateYMV = useTransform(scrollYProgress, [0, 1], props?.translateY ?? [0, 0])
  const translateXMV = useTransform(scrollYProgress, [0, 1], props?.translateX ?? [0, 0])
  const scaleMV = useTransform(scrollYProgress, [0, 1], props?.scale ?? [0, 0])
  const rotateMV = useTransform(scrollYProgress, [0, 1], props?.rotate ?? [0, 0])
  const blurFrom = props?.blur?.[0] ?? 0
  const blurTo = props?.blur?.[1] ?? 0
  const filterMV = useTransform(
    scrollYProgress,
    [0, 1],
    [`blur(${blurFrom}px)`, `blur(${blurTo}px)`],
  )

  const style = useMemo(() => {
    if (!isScrollProgress || !props) return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const out: Record<string, MotionValue<any>> = {}
    if (props.opacity) out.opacity = opacityMV
    if (props.translateY) out.y = translateYMV
    if (props.translateX) out.x = translateXMV
    if (props.scale) out.scale = scaleMV
    if (props.rotate) out.rotate = rotateMV
    if (props.blur) out.filter = filterMV
    return out
  }, [
    isScrollProgress,
    props,
    opacityMV,
    translateYMV,
    translateXMV,
    scaleMV,
    rotateMV,
    filterMV,
  ])

  return { ref, style, isScrollProgress }
}
